import { useEffect } from "react";
import { formatCents } from "../lib/money";
import { lerp, useGrow } from "../lib/useGrow";
import { kindColor } from "./common";

type Bucket = "spend" | "save";

/** Milliseconds for each of the three phases, run off one shared clock. */
const LEAVE_MS = 620;
const TRAVEL_MS = 520;
const LAND_MS = 720;
/** A beat either side of the journey, so the three steps read as three steps. */
const GAP_MS = 140;

const TRAVEL_AT = LEAVE_MS + GAP_MS;
const LAND_AT = TRAVEL_AT + TRAVEL_MS + GAP_MS;

/** Where each vessel's money sits, in its 100x84 user space: the wallet's body
 *  and the piggy bank's belly are not the same band. */
const WALLET_TOP = 26;
const WALLET_HEIGHT = 46;
const PIGGY_TOP = 22;
const PIGGY_HEIGHT = 48;

function label(bucket: Bucket): string {
  return bucket === "save" ? "Save" : "Spend";
}

/**
 * The two places money can be, drawn as the things they are: Spend is a wallet,
 * Save is a piggy bank.
 *
 * Each is filled to its share of the child's total rather than to an absolute
 * amount, because that is the thing a transfer actually changes — the two
 * vessels together always hold one whole circle's worth of money, before and
 * after. The clip id is keyed by bucket, which is unique because the source and
 * the destination are never the same bucket and only one of these dialogs is
 * ever open.
 */
function Vessel({
  bucket,
  fraction,
  cents,
  scale,
}: {
  bucket: Bucket;
  fraction: number;
  cents: number;
  /** Subtle dip as money leaves, subtle pop as it lands. */
  scale: number;
}) {
  const color = kindColor(bucket);
  const clipId = `move-level-${bucket}`;
  const full = Math.max(0, Math.min(1, fraction));

  return (
    <div className="move-bucket">
      <svg
        className="move-bucket__art"
        viewBox="0 0 100 84"
        aria-hidden="true"
        style={{ transform: `scale(${scale})` }}
      >
        <defs>
          <clipPath id={clipId}>
            {bucket === "save" ? (
              <ellipse cx="48" cy="46" rx="32" ry="24" />
            ) : (
              <rect x="8" y="26" width="84" height="46" rx="10" />
            )}
          </clipPath>
        </defs>

        {bucket === "save" ? (
          <PiggyBank color={color} clipId={clipId} full={full} />
        ) : (
          <Wallet color={color} clipId={clipId} full={full} />
        )}
      </svg>
      <span className="move-bucket__name" style={{ color }}>
        {label(bucket)}
      </span>
      <span className="move-bucket__amount">{formatCents(cents)}</span>
    </div>
  );
}

/** The money already in a vessel: a level that rises from its floor. */
function Level({
  color,
  clipId,
  top,
  height,
  full,
}: {
  color: string;
  clipId: string;
  top: number;
  height: number;
  full: number;
}) {
  return (
    <g clipPath={`url(#${clipId})`}>
      <rect
        x="0"
        y={top + height * (1 - full)}
        width="100"
        height={height * full}
        fill={color}
        opacity="0.85"
      />
    </g>
  );
}

function Wallet({
  color,
  clipId,
  full,
}: {
  color: string;
  clipId: string;
  full: number;
}) {
  return (
    <g fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round">
      {/* A banknote poking out of the top, so the shape reads as a wallet and
          not as a box even when it is nearly empty. */}
      <g transform="rotate(-7 44 20)">
        <rect x="24" y="10" width="42" height="18" rx="2" fill="var(--c-surface)" />
        <circle cx="45" cy="19" r="4" />
      </g>
      <rect
        x="8"
        y="26"
        width="84"
        height="46"
        rx="10"
        fill="var(--c-surface-alt)"
      />
      <Level
        color={color}
        clipId={clipId}
        top={WALLET_TOP}
        height={WALLET_HEIGHT}
        full={full}
      />
      <rect x="8" y="26" width="84" height="46" rx="10" />
      {/* The clasp strap, flush with the right edge. */}
      <rect x="60" y="40" width="32" height="18" rx="9" fill="var(--c-surface)" />
      <circle cx="76" cy="49" r="3.5" fill={color} stroke="none" />
    </g>
  );
}

function PiggyBank({
  color,
  clipId,
  full,
}: {
  color: string;
  clipId: string;
  full: number;
}) {
  return (
    <g fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round">
      {/* Behind the body: ear, tail, legs. */}
      <path d="M54 26 L64 12 L70 28 Z" fill="var(--c-surface-alt)" />
      <path d="M16 42 q-9 -3 -5 -9 q4 -5 8 1" strokeLinecap="round" />
      <rect x="26" y="60" width="13" height="16" rx="5" fill="var(--c-surface-alt)" />
      <rect x="57" y="60" width="13" height="16" rx="5" fill="var(--c-surface-alt)" />

      <ellipse cx="48" cy="46" rx="32" ry="24" fill="var(--c-surface-alt)" />
      <Level
        color={color}
        clipId={clipId}
        top={PIGGY_TOP}
        height={PIGGY_HEIGHT}
        full={full}
      />
      <ellipse cx="48" cy="46" rx="32" ry="24" />

      {/* Snout, eye, and the slot the coin drops into. */}
      <ellipse cx="82" cy="50" rx="11" ry="9" fill="var(--c-surface)" />
      <circle cx="79" cy="50" r="1.6" fill={color} stroke="none" />
      <circle cx="85" cy="50" r="1.6" fill={color} stroke="none" />
      <circle cx="64" cy="38" r="2.6" fill={color} stroke="none" />
      <rect x="34" y="24" width="24" height="5" rx="2.5" fill="var(--c-surface)" />
    </g>
  );
}

/**
 * What a completed transfer looks like.
 *
 * Three steps off one clock, for the same reason SpendConfirm has two: shown
 * all at once this would be a pair of numbers, and shown in sequence it is an
 * explanation. A coin visibly leaves the wallet, arcs across, and drops through
 * the piggy bank's slot — or the other way around — and because both vessels
 * are filled to their share of the same unchanged total, the picture makes the
 * point the copy also makes, that moving money does not create any.
 *
 * The move has already happened by the time this renders. There is nothing to
 * confirm and nothing that can fail here; this is feedback, which is what the
 * flow used to be missing entirely.
 */
export function TransferDone({
  amountCents,
  from,
  fromBeforeCents,
  toBeforeCents,
  replayKey,
  onClose,
}: {
  amountCents: number;
  from: Bucket;
  fromBeforeCents: number;
  toBeforeCents: number;
  /** Changes per transfer, so a second move replays instead of showing an
   *  already-finished chart. */
  replayKey: string;
  onClose: () => void;
}) {
  const leaving = useGrow(LEAVE_MS, 0, replayKey);
  const travelling = useGrow(TRAVEL_MS, TRAVEL_AT, replayKey);
  const landing = useGrow(LAND_MS, LAND_AT, replayKey);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const to: Bucket = from === "save" ? "spend" : "save";
  const total = fromBeforeCents + toBeforeCents;
  const fromAfter = fromBeforeCents - amountCents;
  const toAfter = toBeforeCents + amountCents;

  // A child with nothing anywhere cannot get here — the buttons that open this
  // flow are disabled at zero — but dividing by the total demands the guard.
  const share = (cents: number) => (total > 0 ? cents / total : 0);

  const fromCents = lerp(fromBeforeCents, fromAfter, leaving);
  const toCents = lerp(toBeforeCents, toAfter, landing);

  // Spend is on the left and Save on the right, everywhere in the app — the
  // bucket cards, the ledger, here. So the sides are fixed and the *direction*
  // of the journey is what changes: money going back to Spend travels right to
  // left, which is the point being made.
  const spendLeaving = from === "spend";
  const sourceX = spendLeaving ? 18 : 82;
  const targetX = spendLeaving ? 82 : 18;

  const spendCents = spendLeaving ? fromCents : toCents;
  const saveCents = spendLeaving ? toCents : fromCents;
  // The source dips as money leaves it; the destination pops as it lands.
  const sourceScale = 1 - Math.sin(Math.PI * leaving) * 0.04;
  const targetScale = 1 + Math.sin(Math.PI * landing) * 0.06;

  // The coin's journey, as one vertical offset: it lifts out of the source,
  // arcs over the gap, then drops into the destination. `travelling` is already
  // eased, and a sine over it is still zero at both ends, so the arc meets the
  // vessels flat however the easing is tuned.
  const lift = (1 - leaving) * 16;
  const arc = -Math.sin(Math.PI * travelling) * 26;
  const drop = landing * 20;
  const coinFade =
    landing > 0.5 ? Math.max(0, 1 - (landing - 0.5) / 0.3) : Math.min(1, leaving * 1.5);

  return (
    <div className="spend-backdrop" role="presentation">
      <div
        className="spend-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={
          `Moved ${formatCents(amountCents)} from ${label(from)} to ` +
          `${label(to)}. Your total is still ${formatCents(total)}.`
        }
      >
        <h2 className="spend-dialog__title">
          Moved {formatCents(amountCents)}
        </h2>
        <p className="spend-dialog__note">
          from {label(from)} to {label(to)}
        </p>

        <div className="move-flow" aria-hidden="true">
          <Vessel
            bucket="spend"
            fraction={share(spendCents)}
            cents={Math.round(spendCents)}
            scale={spendLeaving ? sourceScale : targetScale}
          />

          <div className="move-track">
            <span className="move-track__line" />
            <span className="move-track__amount">
              {formatCents(amountCents)}
            </span>
          </div>

          {/* Positioned against the whole row, not the gap, so the ends of the
              journey are the vessels themselves: the coin lifts out of one and
              drops into the other. Both columns are 1fr, so the centres stay at
              roughly these fractions however wide the dialog is. */}
          <span
            className="move-coin"
            style={{
              left: `${lerp(sourceX, targetX, travelling)}%`,
              transform: `translate(-50%, ${lift + arc + drop}px)`,
              opacity: coinFade,
              background: kindColor(to),
            }}
          >
            $
          </span>

          <Vessel
            bucket="save"
            fraction={share(saveCents)}
            cents={Math.round(saveCents)}
            scale={spendLeaving ? targetScale : sourceScale}
          />
        </div>

        <p className="move-total">
          Your total didn't change — still{" "}
          <strong>{formatCents(total)}</strong>.
        </p>

        <div className="row move-actions">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
